import React from "react";
import { formatBRL } from "../lib/api";
import { Briefcase, TrendingUp, Landmark, HandCoins } from "lucide-react";

/**
 * HeroKpi — replica fielmente o card "Capital na Rua" do Dashboard executivo:
 * gradiente diagonal, brilho radial no canto, label tracking-[0.22em],
 * valor gigante font-display, borda colorida.
 *
 * 4 variantes (variant): "investido" | "rua" | "disponivel" | "lucro"
 */
export function HeroKpi({ label, value, sub, variant, testid, valueColor, principal = false, dim = false }) {
  const palettes = {
    investido: {
      icon: Briefcase,
      borderColor: "rgba(212,175,55,0.50)",
      labelColor: "text-[#D4AF37]",
      valueColor: "text-white",
      gradient:
        "linear-gradient(135deg, rgba(212,175,55,0.16) 0%, rgba(212,175,55,0.05) 35%, rgba(60,40,10,0.04) 70%, rgba(0,0,0,0) 100%)",
      glow: "radial-gradient(circle, rgba(212,175,55,0.32) 0%, rgba(212,175,55,0) 70%)",
    },
    rua: {
      icon: TrendingUp,
      borderColor: "rgba(212,175,55,0.50)",
      labelColor: "text-[#D4AF37]",
      valueColor: "text-[#E8C547]",
      gradient:
        "linear-gradient(135deg, rgba(212,175,55,0.18) 0%, rgba(212,175,55,0.06) 35%, rgba(60,40,10,0.04) 70%, rgba(0,0,0,0) 100%)",
      glow: "radial-gradient(circle, rgba(212,175,55,0.35) 0%, rgba(212,175,55,0) 70%)",
    },
    disponivel: {
      icon: Landmark,
      borderColor: "rgba(16,185,129,0.45)",
      labelColor: "text-emerald-300",
      valueColor: "text-emerald-300",
      gradient:
        "linear-gradient(135deg, rgba(16,185,129,0.15) 0%, rgba(16,185,129,0.05) 35%, rgba(0,40,30,0.04) 70%, rgba(0,0,0,0) 100%)",
      glow: "radial-gradient(circle, rgba(16,185,129,0.28) 0%, rgba(16,185,129,0) 70%)",
    },
    lucro: {
      icon: HandCoins,
      borderColor: "rgba(16,185,129,0.50)",
      labelColor: "text-emerald-300",
      valueColor: "text-emerald-300",
      gradient:
        "linear-gradient(135deg, rgba(16,185,129,0.20) 0%, rgba(16,185,129,0.07) 35%, rgba(0,40,30,0.04) 70%, rgba(0,0,0,0) 100%)",
      glow: "radial-gradient(circle, rgba(16,185,129,0.35) 0%, rgba(16,185,129,0) 70%)",
    },
    info: {
      icon: TrendingUp,
      borderColor: "rgba(59,130,246,0.40)",
      labelColor: "text-blue-300",
      valueColor: "text-blue-200",
      gradient:
        "linear-gradient(135deg, rgba(59,130,246,0.14) 0%, rgba(59,130,246,0.04) 35%, rgba(10,20,40,0.04) 70%, rgba(0,0,0,0) 100%)",
      glow: "radial-gradient(circle, rgba(59,130,246,0.28) 0%, rgba(59,130,246,0) 70%)",
    },
  };
  let p = palettes[variant] || palettes.investido;

  // Lucro: quando zero, fica visualmente discreto; quando > 0, verde forte.
  if (variant === "lucro" && dim) {
    p = {
      ...p,
      borderColor: "rgba(115,115,115,0.30)",
      labelColor: "text-[#A3A3A3]",
      valueColor: "text-[#A3A3A3]",
      gradient: "linear-gradient(135deg, rgba(40,40,40,0.30) 0%, rgba(0,0,0,0) 70%)",
      glow: "radial-gradient(circle, rgba(115,115,115,0.10) 0%, rgba(0,0,0,0) 70%)",
    };
  }

  const Icon = p.icon;
  // Auto-shrink baseado em comprimento.
  // REGRA: sempre 1 linha, sem ellipsis, sem quebra. Apenas font-size + weight reduzem.
  const valStr = typeof value === "string" ? value : String(value || "");
  const valLen = valStr.length;
  // Faixas conservadoras: garante caber em ~210px de largura útil interna no breakpoint lg.
  // Usa font-display (largura ≈ 0.55 do font-size) → fontSize × chars × 0.55 ≤ usable_width.
  let normalSize, principalSize, weightClass;
  if (valLen <= 9) {
    normalSize = "text-[32px] sm:text-[38px] lg:text-[42px]";
    principalSize = "text-[36px] sm:text-[42px] lg:text-[48px]";
    weightClass = "font-bold";
  } else if (valLen <= 12) {
    normalSize = "text-[26px] sm:text-[30px] lg:text-[32px]";
    principalSize = "text-[28px] sm:text-[34px] lg:text-[36px]";
    weightClass = "font-bold";
  } else if (valLen <= 15) {
    normalSize = "text-[22px] sm:text-[24px] lg:text-[26px]";
    principalSize = "text-[24px] sm:text-[28px] lg:text-[30px]";
    weightClass = "font-semibold";
  } else if (valLen <= 18) {
    normalSize = "text-[18px] sm:text-[20px] lg:text-[22px]";
    principalSize = "text-[20px] sm:text-[22px] lg:text-[24px]";
    weightClass = "font-semibold";
  } else {
    normalSize = "text-[15px] sm:text-[17px] lg:text-[18px]";
    principalSize = "text-[16px] sm:text-[19px] lg:text-[20px]";
    weightClass = "font-semibold";
  }
  const paddingClass = principal ? "p-7 sm:p-9" : "p-6 sm:p-8";
  const valueSize = principal ? principalSize : normalSize;

  return (
    <div
      className={`fc-card fc-card-hover ${paddingClass} relative overflow-hidden`}
      style={{
        background: p.gradient,
        borderColor: p.borderColor,
        boxShadow: principal ? `0 0 0 1px ${p.borderColor}, 0 10px 40px -10px ${p.borderColor}` : undefined,
      }}
      data-testid={testid}
    >
      <div
        className={`absolute -top-12 -right-12 ${principal ? "w-56 h-56" : "w-48 h-48"} rounded-full opacity-30 pointer-events-none`}
        style={{ background: p.glow }}
      />
      <div className="relative">
        <div className={`text-[11px] uppercase tracking-[0.22em] font-semibold flex items-center gap-2 ${p.labelColor}`}>
          <Icon size={13} strokeWidth={1.8} />
          {label}
          {principal && (
            <span className="ml-2 px-1.5 py-0.5 rounded text-[9px] tracking-[0.18em] bg-[#D4AF37]/15 text-[#D4AF37] border border-[#D4AF37]/30">
              PRINCIPAL
            </span>
          )}
        </div>
        <div
          className={`fc-kpi-value font-display ${valueSize} ${weightClass} leading-none mt-4 tracking-tight whitespace-nowrap ${valueColor || p.valueColor}`}
        >
          {value}
        </div>
        {sub && <div className="text-[12.5px] text-[#A3A3A3] mt-3">{sub}</div>}
      </div>
    </div>
  );
}

/**
 * AllocationBar — mantém o estilo executivo (fc-card com borda dourada
 * discreta e padding generoso). Mostra proporção alocado vs disponível.
 */
export function AllocationBar({ investido, naRua, disponivel, testid }) {
  const total = Math.max(investido || 0, (naRua || 0) + Math.max(disponivel || 0, 0));
  const pctRua = total > 0 ? Math.min((naRua / total) * 100, 100) : 0;
  const pctDisp = total > 0 ? Math.max(100 - pctRua, 0) : 0;
  const negativeDisp = (disponivel || 0) < 0;

  return (
    <div className="fc-card p-5 sm:p-6" data-testid={testid}>
      <div className="flex items-center justify-between mb-4">
        <div className="text-[11px] uppercase tracking-[0.22em] text-[#737373] font-semibold">
          Alocação do capital
        </div>
        <div className="text-[11.5px] text-[#A3A3A3] font-mono-tab">
          Base: <span className="text-white">{formatBRL(total)}</span>
        </div>
      </div>

      <div className="h-3.5 rounded-full overflow-hidden bg-[#1A1A1A] border border-[#262626] flex">
        <div
          className="h-full transition-all"
          style={{ width: `${pctRua}%`, background: "linear-gradient(90deg, #997A15 0%, #D4AF37 100%)" }}
          title={`Capital na Rua: ${pctRua.toFixed(1)}%`}
        />
        <div
          className="h-full transition-all"
          style={{
            width: `${pctDisp}%`,
            background: negativeDisp
              ? "linear-gradient(90deg, #7f1d1d 0%, #ef4444 100%)"
              : "linear-gradient(90deg, #047857 0%, #10b981 100%)",
          }}
          title={`Capital Disponível: ${pctDisp.toFixed(1)}%`}
        />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-5">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="w-2.5 h-2.5 rounded-sm bg-[#D4AF37]"/>
            <span className="text-[10px] uppercase tracking-wider text-[#737373]">Alocado</span>
          </div>
          <div className="font-mono-tab text-[15px] text-[#E8C547] font-semibold">{pctRua.toFixed(1)}%</div>
        </div>
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className={`w-2.5 h-2.5 rounded-sm ${negativeDisp ? "bg-red-500" : "bg-emerald-500"}`}/>
            <span className="text-[10px] uppercase tracking-wider text-[#737373]">
              {negativeDisp ? "Negativo" : "Disponível"}
            </span>
          </div>
          <div className={`font-mono-tab text-[15px] font-semibold ${negativeDisp ? "text-red-300" : "text-emerald-300"}`}>
            {pctDisp.toFixed(1)}%
          </div>
        </div>
        <div className="hidden sm:block">
          <div className="text-[10px] uppercase tracking-wider text-[#737373] mb-1">Na rua</div>
          <div className="font-mono-tab text-[14px] text-white">{formatBRL(naRua)}</div>
        </div>
        <div className="hidden sm:block">
          <div className="text-[10px] uppercase tracking-wider text-[#737373] mb-1">Livre</div>
          <div className={`font-mono-tab text-[14px] ${negativeDisp ? "text-red-300" : "text-emerald-300"}`}>
            {formatBRL(disponivel)}
          </div>
        </div>
      </div>
    </div>
  );
}
